import React, { useState } from 'react';
import { useSchoolData, MIN_SCHOOL_QUERY_CHARS, MAX_SCHOOL_RESULTS } from '../hooks/useSchoolData';
import Button from './Button';
import Combobox from './Combobox';

/**
 * School autocomplete, built on the shared Combobox (W3C combobox pattern:
 * typing never commits, a pick is an explicit act, the input is never
 * trimmed). Tapping a suggestion fills the parent's city/state/zip; typing
 * freeform clears them (the submit handler does an exactMatch enrichment so
 * freeform names that uniquely resolve still get their canonical metadata).
 *
 * `onChange({ name, city, state, zip })` fires on typing (companions cleared)
 * and on a pick (all four set) — the same contract as before, so every form
 * that uses this keeps working.
 */
export default function SchoolPicker({
  value,
  city,
  state,
  zip,
  onChange, // (next: { name, city, state, zip }) => void
  role = 'player',
  placeholder,
  preferState, // optional 2-letter state listed first (the athlete's home state)
  disabled,
  className, // optional class applied to the text input (e.g. ae-field__input)
  inputId, // optional id for the input, so a <label htmlFor> can point at it
  onCommitText, // optional (text) => void: free text, once, when the field is left
}) {
  const [results, setResults] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const { search } = useSchoolData(role);

  const handleType = (next) => {
    // Typing diverges from any prior pick — clear companions so the submit
    // never ships stale city/state for an edited name. They get re-set on a
    // pick or via exactMatch enrichment at submit time.
    onChange({ name: next, city: '', state: '', zip: '' });
    if (next.trim().length >= MIN_SCHOOL_QUERY_CHARS) {
      // Ask for one more than we show: that is how we know the list was cut.
      const hits = search(next, MAX_SCHOOL_RESULTS + 1, { preferState });
      setResults(hits.slice(0, MAX_SCHOOL_RESULTS));
      setHasMore(hits.length > MAX_SCHOOL_RESULTS);
    } else {
      setResults([]);
      setHasMore(false);
    }
  };

  const handlePick = (entry) => {
    onChange({
      name: entry.name,
      city: entry.city || '',
      state: entry.state || '',
      zip: entry.zip || '',
    });
    setResults([]);
    setHasMore(false);
  };

  const handleClear = () => {
    onChange({ name: '', city: '', state: '', zip: '' });
    setResults([]);
    setHasMore(false);
  };

  return (
    <div className="school-picker">
      <div className="school-picker__field">
        <Combobox
          value={value || ''}
          onInputChange={handleType}
          items={results}
          getKey={(entry) => entry.id}
          renderItem={(entry) => (
            <>
              <div className="school-picker__name">{entry.name}</div>
              <div className="school-picker__subtitle">{entry.subtitle}</div>
            </>
          )}
          onSelect={handlePick}
          onCommitText={onCommitText}
          inputProps={inputId ? { id: inputId } : undefined}
          footer={hasMore
            ? `Showing the first ${MAX_SCHOOL_RESULTS} matches — keep typing (name, city or state) to narrow`
            : null}
          placeholder={placeholder || (role === 'coach' ? 'Search school…' : 'Search high school…')}
          ariaLabel={role === 'coach' ? 'School' : 'High school'}
          disabled={disabled}
          classNames={{
            input: className,
            popup: 'school-picker__results',
            option: 'school-picker__result',
            optionActive: 'school-picker__result--active',
            footer: 'school-picker__hint',
          }}
        >
          {value && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              iconOnly
              onClick={handleClear}
              aria-label="Clear school"
              tabIndex={-1}
              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}
            >
              ×
            </Button>
          )}
        </Combobox>
      </div>

      {/* Hidden mirror — keeps the parent form's city/state/zip in sync with
          whatever the picker last set. Visible-only render path so consumers
          can pluck the values directly off form state. */}
      {(city || state || zip) && (
        <div className="school-picker__resolved" aria-hidden="true">
          {[city, state, zip].filter(Boolean).join(' · ')}
        </div>
      )}
    </div>
  );
}
